"use client";

import { useQuery } from "@tanstack/react-query";
import { defaultTo, get } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, type RefObject } from "react";
import { useTranslations } from "use-intl";

import { AdminOfferDiscoveryButton } from "@/components/admin-offer-discovery-button";
import { AlbumCoverWithMedia } from "@/components/album-cover-with-media";
import { ArtistLinks } from "@/components/artist-links";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CatalogCreditsCompaniesFaq } from "@/components/catalog-credits-companies-faq";
import { CatalogCreditsCompaniesSection } from "@/components/catalog-credits-companies-section";
import { CatalogFaqAccordion } from "@/components/catalog-faq-accordion";
import { CatalogPricingFaq } from "@/components/catalog-pricing-faq";
import { FacetFilterBar } from "@/components/facets/facet-filter-bar";
import { FeaturePageBacklinks } from "@/components/feature-page-backlinks/feature-page-backlinks";
import { FormatIcon } from "@/components/format-icon";
import { GenreStyleTags } from "@/components/genre-style-tags";
import { Link } from "@/components/link";
import { CatalogListActions } from "@/components/lists/CatalogListActions";
import { CollectionStateChip } from "@/components/lists/collection-state-chip";
import { CatalogDealOffers } from "@/components/marketplace/CatalogDealOffers";
import { getDealOfferSummaryQueryOptions } from "@/components/marketplace/deal-offers-query";
import { WorkMarketplaceSection } from "@/components/marketplace/listings-section";
import { AlertsCallout } from "@/components/messaging/alerts-callout";
import { DetailActions } from "@/components/music/detail-actions";
import { DetailBackLink } from "@/components/music/detail-back-link";
import { type DetailFact, DetailFactsRow } from "@/components/music/detail-facts-row";
import { DetailHeroLayout } from "@/components/music/detail-hero-layout";
import { DetailPageShell } from "@/components/music/detail-page-shell";
import { LastfmScrobbleButton } from "@/components/music/lastfm-scrobble-button";
import { MusicTypeChip } from "@/components/music/music-type-chip";
import { SourceLinks } from "@/components/music/source-links";
import { getSpotifyAlbumQueryOptions } from "@/components/music/spotify-album-query";
import { TracklistSection } from "@/components/music/tracklist-section";
import { PageJumpNav, type PageJumpNavItem } from "@/components/page-jump-nav";
import { PlayAllButton } from "@/components/play-all-button";
import { ShareButton } from "@/components/share-button";
import { SpotifyPlaybackButton } from "@/components/SpotifyPlaybackButton";
import { Tracklist } from "@/components/tracklist";
import { type FilterOption } from "@/components/ui/filter-select";
import { WikipediaSection } from "@/components/wikipedia-section";
import { WORK_SURFACE } from "@/facets/config";
import { useCurrency } from "@/hooks/use-currency";
import { useDeferredResourceQuery } from "@/hooks/use-deferred-resource";
import { useFormatNumber } from "@/hooks/use-format-number";
import { useRecordView } from "@/hooks/use-record-view";
import { useViewerQueryState } from "@/hooks/use-viewer-query-state";
import { useWorkContentController } from "@/hooks/use-work-content-controller";
import { formatDuration } from "@/lib/format-duration";
import {
  getWorkDisambiguationDisplay,
  shouldRenderWorkDisambiguationInline,
} from "@/lib/work-title";
import { orpc } from "@/utils/orpc.client";
import { type AlbumType } from "@garimpa/lib/albumTypes";
import { getPrimaryFormatCategory } from "@garimpa/lib/formatClassification";
import { FORMAT_CATEGORIES_WITH_OTHER } from "@garimpa/lib/formatGroups";

import { WorkReleaseGrid } from "./work-release-grid";
import { WorkReleasePagination } from "./work-release-pagination";

const VISIBLE_SECONDARY_WORK_TYPES = [
  "compilation",
  "live",
] as const satisfies readonly AlbumType[];

const SECTION_IDS = {
  editions: "work-editions",
  tracks: "work-tracks",
  credits: "work-credits",
  faq: "work-faq",
} as const;

function getVisibleSecondaryWorkTypes(
  primaryType: AlbumType | null | undefined,
  secondaryTypes: AlbumType[] | null | undefined,
) {
  const visible = new Set<AlbumType>(VISIBLE_SECONDARY_WORK_TYPES);
  const seen = new Set<AlbumType>();

  return (secondaryTypes ?? []).filter((type) => {
    if (!visible.has(type) || type === primaryType || seen.has(type)) return false;
    seen.add(type);
    return true;
  });
}

function workArtistDisplay(
  artists: Array<{ name: string; joinSeparator?: string | null }> | null | undefined,
) {
  if (!artists) return undefined;
  return artists.map((artist) => `${artist.name}${defaultTo(artist.joinSeparator, "")}`).join("");
}

type WorkContentController = ReturnType<typeof useWorkContentController>;

interface WorkFactsInput {
  durationSeconds: number | null | undefined;
  formatCount: number;
  formatNumber: (value: number) => string;
  hasDuration: boolean;
  releaseCount: number;
  t: ReturnType<typeof useTranslations<"release">>;
  tp: ReturnType<typeof useTranslations<"shared.pageJumpNav">>;
  trackCount: number;
  year: number | null | undefined;
}

function workTrackSummary(input: WorkFactsInput): string | null {
  const parts = [
    input.trackCount > 0 ? input.formatNumber(input.trackCount) : null,
    input.hasDuration ? formatDuration(defaultTo(input.durationSeconds, 0)) : null,
  ].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(" · ") : null;
}

/**
 * Fixed-length facts only. Per-format counts live in the editions filter, which
 * already copes with many formats and large counts; here it is "N in K formats".
 */
function buildWorkFacts(input: WorkFactsInput): DetailFact[] {
  const { t, tp } = input;
  const trackSummary = workTrackSummary(input);
  const facts: Array<DetailFact | null> = [
    input.year
      ? {
          key: "year",
          label: t("originalYear"),
          value: <time dateTime={String(input.year)}>{input.year}</time>,
        }
      : null,
    trackSummary ? { key: "tracks", label: t("tracks"), value: trackSummary } : null,
    input.releaseCount > 0
      ? {
          key: "editions",
          label: tp("editions"),
          value: (
            <a href={`#${SECTION_IDS.editions}`} className="text-primary hover:underline">
              {tp("editionsInFormats", {
                count: input.releaseCount,
                formats: input.formatCount,
              })}
            </a>
          ),
        }
      : null,
  ];
  return facts.filter((fact): fact is DetailFact => fact !== null);
}

function hasWorkCredits(
  creditsByRole: Record<string, unknown[]> | null | undefined,
  companyCount: number,
): boolean {
  return (
    Object.values(creditsByRole ?? {}).some((credits) => credits.length > 0) || companyCount > 0
  );
}

/** Artist → title (+ disambiguation) → type and collection chips. */
function WorkIdentity({
  id,
  isInCollection,
  isInWishlist,
  work,
}: {
  id: string;
  isInCollection: WorkContentController["isInCollection"];
  isInWishlist: WorkContentController["isInWishlist"];
  work: WorkContentController["work"];
}) {
  const tcol = useTranslations("shared.collection");
  const tl = useTranslations("globals.lists");
  const displayDisambiguation = getWorkDisambiguationDisplay(work.title, work.disambiguation);
  const renderDisambiguationInline = Boolean(
    displayDisambiguation && shouldRenderWorkDisambiguationInline(displayDisambiguation),
  );
  const secondaryWorkTypes = getVisibleSecondaryWorkTypes(work.type, work.secondaryTypes);

  return (
    <div className="space-y-3">
      {get(work.artists, "length", 0) > 0 && (
        <p className="text-lg font-bold md:text-xl">
          <ArtistLinks appearance="text" artists={work.artists} className="text-primary" />
        </p>
      )}
      <div className="space-y-1">
        <h1 className="text-4xl font-black leading-none tracking-tighter hyphens-auto [overflow-wrap:anywhere] md:text-5xl lg:text-6xl">
          {work.title}
          {renderDisambiguationInline && (
            <>
              {" "}
              <span className="ml-3 inline-block align-baseline text-xl font-semibold tracking-normal text-muted-foreground md:text-2xl lg:text-3xl">
                {displayDisambiguation}
              </span>
            </>
          )}
        </h1>
        {displayDisambiguation && !renderDisambiguationInline && (
          <p className="max-w-3xl text-base font-semibold leading-snug text-muted-foreground md:text-lg">
            {displayDisambiguation}
          </p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <MusicTypeChip type={work.type} fallbackToRelease />
        {secondaryWorkTypes.map((type) => (
          <MusicTypeChip key={type} type={type} tone="secondary" />
        ))}
        <CollectionStateChip
          inCollection={isInCollection({ workId: id })}
          inWishlist={isInWishlist({ workId: id })}
          collectionLabel={tcol("inCollection")}
          wishlistLabel={tl("inWishlist")}
        />
      </div>
    </div>
  );
}

interface WorkReleasesPanelProps {
  facetControls: WorkContentController["facetControls"];
  facetOptions: Record<string, FilterOption[]>;
  filteredReleases: WorkContentController["releases"];
  formatCounts: WorkContentController["formatCounts"];
  getFormatLinkTarget: WorkContentController["getFormatLinkTarget"];
  getReleasePageLinkTarget: WorkContentController["getReleasePageLinkTarget"];
  handleFormatChange: WorkContentController["handleFormatChange"];
  handleSearchChange: WorkContentController["handleSearchChange"];
  headingRef: RefObject<HTMLHeadingElement | null>;
  isReleasesFetching: boolean;
  onPageNavigate: (page: number) => void;
  releasePage: number;
  releasePageCount: number;
  releaseResultCount: number;
  selectedFormat: WorkContentController["selectedFormat"];
  selectedSearch: WorkContentController["selectedSearch"];
}

function WorkReleasesPanel({
  facetControls,
  facetOptions,
  filteredReleases,
  formatCounts,
  getFormatLinkTarget,
  getReleasePageLinkTarget,
  handleFormatChange,
  handleSearchChange,
  headingRef,
  isReleasesFetching,
  onPageNavigate,
  releasePage,
  releasePageCount,
  releaseResultCount,
  selectedFormat,
  selectedSearch,
}: WorkReleasesPanelProps) {
  const t = useTranslations("album");
  const tc = useTranslations("globals.common");
  const tp = useTranslations("globals.search");
  const trf = useTranslations("shared.releaseFilters");
  const hasReleases = filteredReleases.length > 0;
  const hasSelectedFormat = selectedFormat !== "all";

  return (
    <section
      id={SECTION_IDS.editions}
      aria-labelledby="work-editions-title"
      aria-busy={isReleasesFetching}
      className="scroll-mt-36 pt-10"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2 pb-2">
        <h2
          id="work-editions-title"
          ref={headingRef}
          tabIndex={-1}
          className="text-2xl font-bold outline-none"
        >
          {t("availableReleases")}
        </h2>
        <span className="text-muted-foreground text-sm" aria-live="polite">
          {t("releaseCount", { count: releaseResultCount })}
          {hasSelectedFormat && ` (${trf(selectedFormat)})`}
          {releasePageCount > 1 &&
            ` · ${tp("pageOf", { page: releasePage, total: releasePageCount })}`}
        </span>
      </div>

      <FacetFilterBar
        surface={WORK_SURFACE}
        options={facetOptions}
        controller={facetControls}
        formatValue={selectedFormat}
        onFormatChange={handleFormatChange}
        formatLinkTarget={getFormatLinkTarget}
        formatTotalCount={formatCounts.all}
        search={selectedSearch}
        onSearchChange={handleSearchChange}
        searchPlaceholder={trf("searchPlaceholder")}
        layout="compact"
      />

      {hasReleases ? (
        <div>
          <WorkReleaseGrid releases={filteredReleases} isFetching={isReleasesFetching} />
          <WorkReleasePagination
            currentPage={releasePage}
            totalPages={releasePageCount}
            isFetching={isReleasesFetching}
            pageLabel={tp("pageOf", { page: releasePage, total: releasePageCount })}
            previousLabel={tp("previousPage")}
            nextLabel={tp("nextPage")}
            getPageLinkTarget={getReleasePageLinkTarget}
            onPageNavigate={onPageNavigate}
          />
        </div>
      ) : (
        <div className="text-center py-12">
          <FormatIcon
            format={selectedFormat}
            size={48}
            className="mx-auto text-muted-foreground mb-4"
          />
          <p className="text-muted-foreground">
            {hasSelectedFormat
              ? t("noReleasesFound", { format: trf(selectedFormat).toLowerCase() })
              : t("noReleasesFoundAll")}
          </p>
          {hasSelectedFormat && (
            <button
              type="button"
              onClick={() => handleFormatChange("all")}
              className="mt-4 text-primary hover:underline"
            >
              {tc("showAllFormats")}
            </button>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * After a page change, bring the editions heading back under the sticky jump nav
 * (only when it is out of view) and move focus to it so keyboard and screen-reader
 * users land on the new results. Filters and search never scroll.
 */
function useEditionsPageFocus(
  releasePage: number,
  headingRef: RefObject<HTMLHeadingElement | null>,
) {
  const pendingPageRef = useRef<number | null>(null);

  useEffect(() => {
    if (pendingPageRef.current !== releasePage) return;
    pendingPageRef.current = null;

    requestAnimationFrame(() => {
      const heading = headingRef.current;
      if (!heading) return;
      const { top } = heading.getBoundingClientRect();
      const isInView = top >= 0 && top < window.innerHeight * 0.5;
      if (!isInView) {
        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        heading
          .closest("section")
          ?.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
      }
      heading.focus({ preventScroll: true });
    });
  }, [headingRef, releasePage]);

  return useCallback((page: number) => {
    pendingPageRef.current = page;
  }, []);
}

function buildWorkJumpItems(input: {
  hasCredits: boolean;
  hasTracks: boolean;
  releaseCount: number;
  tp: ReturnType<typeof useTranslations<"shared.pageJumpNav">>;
  trackCount: number;
}): PageJumpNavItem[] {
  const { tp } = input;
  const items: Array<PageJumpNavItem | null> = [
    { id: SECTION_IDS.editions, label: tp("editions"), count: input.releaseCount },
    input.hasTracks
      ? { id: SECTION_IDS.tracks, label: tp("tracks"), count: input.trackCount }
      : null,
    input.hasCredits ? { id: SECTION_IDS.credits, label: tp("credits") } : null,
    { id: SECTION_IDS.faq, label: tp("questions") },
  ];
  return items.filter((item): item is PageJumpNavItem => item !== null);
}

export function WorkContent({ id }: { id: string }) {
  useRecordView("work", id);
  const t = useTranslations("album");
  const tr = useTranslations("release");
  const tp = useTranslations("shared.pageJumpNav");
  const ta = useTranslations("shared.detailActions");
  const trf = useTranslations("shared.releaseFilters");
  const tc = useTranslations("globals.common");
  const formatNumber = useFormatNumber();
  const { currency } = useCurrency();
  const { viewerId } = useViewerQueryState();
  const {
    work,
    isInCollection,
    isInWishlist,
    facetControls,
    selectedFormat,
    selectedSearch,
    handleSearchChange,
    handleFormatChange,
    getFormatLinkTarget,
    releases: filteredReleases,
    releasePage,
    releasePageCount,
    releaseResultCount,
    isReleasesFetching,
    getReleasePageLinkTarget,
    formatCounts,
    filterOptions,
    hasPlayableTracks,
    isPlayAllResolving,
    handlePlayAll,
    handlePlayTrack,
    workMedia,
    trackVideos,
  } = useWorkContentController({ id });
  const workCompanies = defaultTo(work.companies, []);
  const editionsHeadingRef = useRef<HTMLHeadingElement>(null);
  const handleReleasePagination = useEditionsPageFocus(releasePage, editionsHeadingRef);

  const facetOptions = useMemo<Record<string, FilterOption[]>>(() => {
    const formatOptions = FORMAT_CATEGORIES_WITH_OTHER.filter(
      (format) => (formatCounts[format] ?? 0) > 0,
    ).map((format) => ({
      value: format,
      label: trf.has(format) ? trf(format) : format,
      count: formatCounts[format],
    }));
    return {
      format: formatOptions,
      formatSet: filterOptions.formatSets,
      country: filterOptions.countries,
      year: filterOptions.years,
      label: filterOptions.labels,
      company: filterOptions.companies,
      tag: filterOptions.tags,
    };
  }, [formatCounts, filterOptions, trf]);

  const trackCount = workMedia.reduce((sum, m) => sum + m.tracks.length, 0);
  const durationSeconds = work.durationSeconds;
  const releaseCount = defaultTo(work.releaseCount, 0);
  const hasDuration = [
    typeof durationSeconds === "number",
    defaultTo(durationSeconds, 0) > 0,
  ].every(Boolean);
  const formatCount = facetOptions.format?.length ?? 0;
  const facts = buildWorkFacts({
    durationSeconds,
    formatCount,
    formatNumber,
    hasDuration,
    releaseCount,
    t: tr,
    tp,
    trackCount,
    year: work.year,
  });
  const hasCredits = hasWorkCredits(work.creditsByRole, workCompanies.length);
  const jumpItems = buildWorkJumpItems({
    hasCredits,
    hasTracks: trackCount > 0,
    releaseCount,
    tp,
    trackCount,
  });
  const mediaVisualCategory = getPrimaryFormatCategory({
    mediaCategories: work.mediaCategories,
  });
  const dealOfferSummaryQuery = useQuery(
    getDealOfferSummaryQueryOptions({ workId: id, currency, format: selectedFormat }, { viewerId }),
  );
  const dealOfferSummarySnapshot = {
    isLoading: dealOfferSummaryQuery.isLoading,
    summary: defaultTo(get(dealOfferSummaryQuery.data, "0"), null),
  };
  const computeSpotifyAlbumResource = useCallback(
    () => orpc.music.computeSpotifyAlbumResource.call({ workId: id }),
    [id],
  );
  const {
    query: spotifyAlbumQuery,
    resource: spotifyAlbumResource,
    value: spotifyAlbum,
  } = useDeferredResourceQuery({
    queryOptions: getSpotifyAlbumQueryOptions({ workId: id }),
    compute: computeSpotifyAlbumResource,
  });

  const spotifyUrl = defaultTo(get(spotifyAlbum, "url"), undefined);
  const isSpotifyResolving = [
    spotifyAlbumQuery.isLoading,
    get(spotifyAlbumResource, "status") === "pending",
  ].some(Boolean);

  return (
    <DetailPageShell>
      {work.artists?.[0] && (
        <DetailBackLink to={`/artist/${work.artists[0].id}`} label={work.artists[0].name} />
      )}
      <Breadcrumbs className="mb-8 hidden md:flex" label={tc("breadcrumbNavigation")}>
        {work.primaryIndex ? (
          <Link to={work.primaryIndex.path} className="text-muted-foreground">
            {work.primaryIndex.label}
          </Link>
        ) : null}
        {get(work.artists, "length", 0) > 0 && (
          <span className="text-muted-foreground">
            <ArtistLinks
              appearance="text"
              artists={work.artists}
              className="text-muted-foreground"
            />
          </span>
        )}
        <span className="font-medium text-primary">{work.title}</span>
      </Breadcrumbs>

      <DetailHeroLayout
        media={
          <div className="lg:col-span-5 relative flex items-start justify-center lg:sticky lg:top-20 lg:self-start overflow-hidden lg:overflow-visible">
            <AlbumCoverWithMedia
              formatCategory={defaultTo(mediaVisualCategory, undefined)}
              imageUrl={work.imageUrl}
              title={work.title}
              artistName={workArtistDisplay(work.artists)}
            >
              <AlbumCoverWithMedia.Media />
              <AlbumCoverWithMedia.Cover />
            </AlbumCoverWithMedia>
          </div>
        }
        content={
          <div className="lg:col-span-7 flex min-w-0 flex-col gap-6">
            <WorkIdentity
              id={id}
              work={work}
              isInCollection={isInCollection}
              isInWishlist={isInWishlist}
            />

            <DetailFactsRow facts={facts} />

            <DetailActions
              listening={
                <PlayAllButton
                  canPlayAll={hasPlayableTracks}
                  isPlayAllResolving={isPlayAllResolving}
                  onClick={handlePlayAll}
                  label={t("listenAlbum")}
                  unavailableLabel={ta("listeningUnavailable")}
                  scrollTargetId={SECTION_IDS.tracks}
                />
              }
              listeningProvider={
                <SpotifyPlaybackButton
                  isResolving={isSpotifyResolving}
                  label={t("openOnSpotify")}
                  url={spotifyUrl}
                  variant="compact"
                  workId={id}
                />
              }
              collection={<CatalogListActions target={{ entityType: "work", workId: id }} />}
              overflow={
                <>
                  <LastfmScrobbleButton
                    entity="work"
                    id={id}
                    variant="pill"
                    className="min-h-11 w-full justify-start rounded-lg border-transparent bg-transparent px-3 hover:bg-[#D51007]/10"
                  />
                  <ShareButton
                    title={work.title}
                    className="min-h-11 w-full justify-start px-3 py-2.5"
                  />
                </>
              }
              admin={<AdminOfferDiscoveryButton catalogId={id} entityType="work" />}
            />

            <CatalogDealOffers
              presentationScope="work"
              workId={id}
              offerSummarySnapshot={dealOfferSummarySnapshot}
              format={selectedFormat}
              formatLinkTarget={getFormatLinkTarget}
              target={{ entityType: "work", workId: id }}
            />
          </div>
        }
      />

      <PageJumpNav items={jumpItems} />

      <WorkReleasesPanel
        facetControls={facetControls}
        facetOptions={facetOptions}
        filteredReleases={filteredReleases}
        formatCounts={formatCounts}
        getFormatLinkTarget={getFormatLinkTarget}
        getReleasePageLinkTarget={getReleasePageLinkTarget}
        handleFormatChange={handleFormatChange}
        handleSearchChange={handleSearchChange}
        headingRef={editionsHeadingRef}
        isReleasesFetching={isReleasesFetching}
        onPageNavigate={handleReleasePagination}
        releasePage={releasePage}
        releasePageCount={releasePageCount}
        releaseResultCount={releaseResultCount}
        selectedFormat={selectedFormat}
        selectedSearch={selectedSearch}
      />

      <div className="grid grid-cols-1 gap-12 pt-16 lg:grid-cols-[minmax(0,1fr)_22rem] lg:gap-16">
        <div className="min-w-0 space-y-10">
          {trackCount > 0 && (
            <div id={SECTION_IDS.tracks} className="scroll-mt-36">
              <TracklistSection title={t("tracklist")} hasTracks emptyLabel={t("noTracks")}>
                <Tracklist media={workMedia} onPlay={handlePlayTrack} trackVideos={trackVideos} />
              </TracklistSection>
            </div>
          )}
          <WorkMarketplaceSection workId={id} />
        </div>
        <aside className="min-w-0 space-y-6" aria-label={tp("aboutAlbum")}>
          <FeaturePageBacklinks entityType="work" catalogId={id} />
          <WikipediaSection wikidataId={work.wikidataId} />
          <GenreStyleTags
            genres={work.genres}
            styles={work.styles}
            pages={work.taxonomyPages}
            className="mb-0 gap-2"
            tagClassName="rounded-full bg-secondary px-3 py-1.5"
          />
          <SourceLinks links={work.sourceLinks} />
        </aside>
      </div>

      {hasCredits && (
        <div id={SECTION_IDS.credits} className="scroll-mt-36 pt-16">
          <CatalogCreditsCompaniesSection
            companies={workCompanies}
            creditsByRole={work.creditsByRole}
          />
        </div>
      )}

      <section
        id={SECTION_IDS.faq}
        aria-labelledby="catalog-faq-title"
        className="scroll-mt-36 pt-16"
      >
        <CatalogFaqAccordion titleId="catalog-faq-title">
          <CatalogPricingFaq
            artist={work.artistDisplay}
            entityType="work"
            summary={dealOfferSummarySnapshot.summary}
            title={work.title}
            workId={id}
          />
          <CatalogCreditsCompaniesFaq
            artist={work.artistDisplay}
            companies={workCompanies}
            companyScope="work"
            creditsByRole={work.creditsByRole}
            title={work.title}
          />
        </CatalogFaqAccordion>
      </section>
      <AlertsCallout surface="work_detail" />
    </DetailPageShell>
  );
}
